import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, Sparkles, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { seedTestData } from "@/lib/seed-test-data";

// Dev login is handled server-side by the dev-auto-login edge function
// No admin email is exposed in client code

export default function AuthPage() {
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem("together_os_email") || ""; } catch { return ""; }
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSendMagicLink = async () => {
    if (!email.trim()) return;
    setLoading(true);

    // Try dev-auto-login for any email - server validates eligibility
    try {
      const { data, error } = await supabase.functions.invoke("dev-auto-login", {
        body: { email: email.toLowerCase() },
      });

      if (data?.hashed_token) {
        // Dev login succeeded - verify token
        const { data: sessionData, error: verifyErr } = await supabase.auth.verifyOtp({
          token_hash: data.hashed_token,
          type: "magiclink",
        });

        if (verifyErr) {
          toast.error("Auth failed", { description: verifyErr.message });
        } else if (sessionData.session) {
          const userId = sessionData.session.user.id;
          const { data: profile } = await supabase
            .from("profiles")
            .select("workspace_id")
            .eq("user_id", userId)
            .single();
          if (profile?.workspace_id) {
            await seedTestData(userId, profile.workspace_id);
          }
          toast.success("Dev login successful ⚡");
        }
        setLoading(false);
        return;
      }
    } catch {
      // Not eligible for dev login, fall through to normal flow
    }

    // Normal magic link flow
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/app`,
      },
    });
    setLoading(false);
    if (error) {
      toast.error("Something went wrong", { description: error.message });
    } else {
      setSent(true);
      toast.success("Check your email", { description: "We sent you a magic link to sign in." });
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="w-7 h-7 text-accent" fill="currentColor" />
            <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight">
              Together OS
            </h1>
          </div>
          <p className="text-muted-foreground text-lg font-display italic">
            Growing together, one reflection at a time.
          </p>
        </div>

        <div className="bg-card border border-border rounded-xl p-8 space-y-6">
          {!sent ? (
            <>
              <div className="space-y-2 text-center">
                <h2 className="font-display text-xl font-semibold text-foreground">Welcome</h2>
                <p className="text-sm text-muted-foreground">
                  Sign in or create your account to begin.
                </p>
              </div>

              <div className="space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-background border border-input rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSendMagicLink();
                  }}
                />
                <button
                  onClick={handleSendMagicLink}
                  disabled={loading || !email.trim()}
                  className="w-full py-3 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  {loading ? "Signing in..." : "Send Magic Link"}
                </button>
              </div>

              <p className="text-xs text-muted-foreground text-center">
                We'll email you a link — no password needed.
              </p>
            </>
          ) : (
            <>
              <div className="space-y-2 text-center">
                <h2 className="font-display text-xl font-semibold text-foreground">Check your email</h2>
                <p className="text-sm text-muted-foreground">
                  We sent a magic link to <span className="font-medium text-foreground">{email}</span>
                </p>
                <p className="text-sm text-muted-foreground">
                  Click the link in your email to sign in.
                </p>
              </div>

              <button
                onClick={() => { setSent(false); }}
                className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Use a different email
              </button>
            </>
          )}
        </div>

        <p className="text-xs text-muted-foreground text-center mt-6">
          Your data is private. Only you and your partner can see it.
        </p>
      </motion.div>
    </div>
  );
}
